import React, { useMemo } from 'react';
import useChartZoom from './useChartZoom';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from '../../lib/charts/highcharts';
import ChartFrame from './ChartFrame';
import { STOCK_CHROME, renderZoom } from './WindTimelineChart';
import { MIN_RANGE_PRESSURE_HPA } from '../../lib/charts/history';
import { LINE_COLORS } from '../../lib/charts/lineColors';
import { zoomingFor } from './zooming';
import { formatStationTime } from '../../lib/charts/time';

/** Pressure alone (synoptic outlook). minRange keeps 0.2 hPa sensor noise from filling the plot. */
export function buildPressureSeries(points, labels) {
    return [{
        name: labels.pressure, type: 'area', color: LINE_COLORS.pressure, lineWidth: 2, connectNulls: false,
        threshold: null, fillOpacity: 0.15, data: points.map((p) => [p.t, p.pressure]),
    }];
}

const PressureChart = ({ points, labels, zoomLabels, shared, zoomTarget, plotBands, zoomResetKey, height = 260, loading, error, emptyMessage, errorMessage, retryLabel, onRetry }) => {
    // zoomResetKey (range/station) resets to All; new points alone keep the preset and slide it.
    const own = useChartZoom(zoomResetKey ?? points, points);
    // `shared` = { chartRef, xAxisEvents } from useSharedZoom: no own zoom control, no own navigator.
    const chartRef = shared ? shared.chartRef : own.chartRef;
    const xAxisEvents = shared ? shared.xAxisEvents : own.xAxisEvents;
    const options = useMemo(() => ({
        chart: { height, backgroundColor: 'transparent', panning: { enabled: true, type: 'x' }, zooming: zoomingFor(shared) },
        ...STOCK_CHROME,
        navigator: { enabled: !shared, height: 36 },
        title: { text: undefined },
        xAxis: { type: 'datetime', crosshair: true, ordinal: false, events: xAxisEvents, plotBands, dateTimeLabelFormats: { hour: '%H:%M', day: '%a %H:%M', week: '%d %b', month: '%d %b' } },
        yAxis: {
            title: { text: 'hPa', align: 'high', rotation: 0, offset: 0, textAlign: 'left', x: 3, y: -3, style: { color: 'var(--chart-pressure)', fontSize: '10px' } },
            opposite: false, tickColor: 'var(--chart-pressure)', lineColor: 'var(--chart-pressure)', tickWidth: 1, tickLength: 6, lineWidth: 1,
            labels: { x: -3, style: { color: 'var(--chart-pressure)', fontSize: '10px' } },
            startOnTick: false, endOnTick: false, minRange: MIN_RANGE_PRESSURE_HPA, tickAmount: 5,
        },
        tooltip: {
            shared: true, useHTML: true, borderRadius: 6, padding: 8,
            formatter() {
                const lines = [`<span style="font-size:10px;color:var(--chart-text-subtle)">${formatStationTime(Number(this.x), 'DD.MM.YYYY HH:mm')}</span>`];
                for (const p of this.points || []) {
                    const v = p.y === null || p.y === undefined ? '—' : p.y.toFixed(1);
                    lines.push(`<span style="color:${p.color}">●</span> ${p.series.name}: <b>${v} hPa</b>`);
                }
                return lines.join('<br>');
            },
        },
        plotOptions: { series: { animation: { duration: 600 }, marker: { enabled: false, states: { hover: { enabled: true } } }, dataGrouping: { enabled: false } } },
        legend: { enabled: false },
        accessibility: { description: 'Atmospheric pressure over time' },
        series: buildPressureSeries(points, labels),
    }), [points, labels, height, xAxisEvents, shared, plotBands]);

    return (
        <div>
            {renderZoom(shared, own, zoomLabels, zoomTarget)}
            <ChartFrame ariaLabel={labels.pressure} height={height} loading={loading} error={error}
                        empty={!points || points.length < 2} emptyMessage={emptyMessage}
                        errorMessage={errorMessage} retryLabel={retryLabel} onRetry={onRetry}>
                <HighchartsReact ref={chartRef} highcharts={Highcharts} constructorType="stockChart" options={options} />
            </ChartFrame>
        </div>
    );
};

export default PressureChart;
